import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import type { SalesLookupFamilyDetail } from './types';

// Non-paginated read model for the mobile detail screen: every variant,
// supplier deal, current public price and recent closed (order-linked quote)
// price for one family, in a single fetch.
const endpoint = 'ums/v1/cms/sales-lookup/families';
export const getSalesLookupFamilyEndpointIdentifier = endpoint;

interface ResponseData {
  data: SalesLookupFamilyDetail;
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, number | undefined, number | undefined];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetSalesLookupFamilyApi(
  familyId: number | undefined,
  restaurantId: number | undefined,
  options?: Options
): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();

  const queryFn = async () => {
    try {
      const { data } = await http.get(`${endpoint}/${familyId}`, { params: { restaurantId } });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([endpoint, familyId, restaurantId], queryFn, {
    enabled: isLoggedIn && !!familyId,
    ...options,
  });
}
